// src/pages/Auditorias/components/FicheirosArvore.js
import React, { useState } from 'react';
import {
  GRAVIDADE_CLASSE, GRAVIDADE_LABEL, formatarTamanho
} from '../utils/formatacao';

/**
 * Árvore de ficheiros da auditoria. É virtual: nada disto existe em disco,
 * tudo vem das colunas de texto em Neon (ver spec, secção 2.3).
 *
 * Os quatro grupos existem para se ver de imediato o que ficou de fora e porquê.
 * Ignorado e ilegível não são a mesma coisa: o primeiro foi decisão da triagem,
 * o segundo é um documento que existe e não conseguimos ler.
 */
const Pasta = ({ nome, contagem, nota, children }) => {
  const [aberta, setAberta] = useState(false);

  if (!contagem) return null;

  return (
    <div className="aud-pasta">
      <button type="button" className="aud-pasta-cabeca" onClick={() => setAberta((v) => !v)}>
        <span aria-hidden="true">{aberta ? '▾' : '▸'}</span>
        <span className="aud-pasta-nome">{nome}</span>
        <span className="aud-pasta-contagem">{contagem}</span>
        {nota && <span className="aud-pasta-nota">{nota}</span>}
      </button>
      {aberta && <div className="aud-pasta-corpo">{children}</div>}
    </div>
  );
};

const FicheirosArvore = ({ ficheiros, onAbrir }) => {
  if (!ficheiros) return <p className="aud-vazio">A carregar…</p>;

  const { pasta, artefactos, avaliacoes, transcricoes, ignorados, ilegiveis } = ficheiros;

  return (
    <div className="aud-arvore">
      <div className="aud-arvore-raiz">{pasta}/</div>

      {artefactos.map((a) => (
        <button
          key={a.id}
          type="button"
          className="aud-ficheiro aud-ficheiro-geral"
          onClick={() => onAbrir({ tipo: 'artefacto', refId: a.id, titulo: a.nome_ficheiro })}
        >
          <span className="aud-ficheiro-nome">{a.nome_ficheiro}</span>
          <span className="aud-ficheiro-meta">{formatarTamanho(a.tamanho)}</span>
        </button>
      ))}

      <Pasta nome="avaliacoes/" contagem={avaliacoes.length}>
        {avaliacoes.map((f) => (
          <button
            key={f.id}
            type="button"
            className="aud-ficheiro"
            onClick={() => onAbrir({ tipo: 'avaliacao', refId: f.id, titulo: f.nome_ficheiro })}
          >
            <span className="aud-ficheiro-nome">{f.nome_ficheiro}</span>
            {f.gravidade && f.gravidade !== 'OK' && (
              <span className={`aud-badge ${GRAVIDADE_CLASSE[f.gravidade] || ''}`}>
                {GRAVIDADE_LABEL[f.gravidade]}
              </span>
            )}
            <span className="aud-ficheiro-meta">{f.nome_original}</span>
          </button>
        ))}
      </Pasta>

      <Pasta nome="transcricoes/" contagem={transcricoes.length}>
        {transcricoes.map((f) => (
          <button
            key={f.id}
            type="button"
            className="aud-ficheiro"
            onClick={() => onAbrir({ tipo: 'transcricao', refId: f.id, titulo: f.nome_ficheiro })}
          >
            <span className="aud-ficheiro-nome">{f.nome_ficheiro}</span>
            {f.tipo_documento === 'anexo' && <span className="aud-badge">anexo</span>}
            <span className="aud-ficheiro-meta">{formatarTamanho(f.tamanho)}</span>
          </button>
        ))}
      </Pasta>

      <Pasta
        nome="ignorados/"
        contagem={ignorados.length}
        nota="não entraram na análise"
      >
        {ignorados.map((f) => (
          <div key={f.id} className="aud-ficheiro aud-ficheiro-inerte">
            <span className="aud-ficheiro-nome">{f.nome_original}</span>
            <span className="aud-ficheiro-meta">{f.motivo}</span>
          </div>
        ))}
      </Pasta>

      <Pasta
        nome="ilegiveis/"
        contagem={ilegiveis.length}
        nota="documentos que não foi possível ler"
      >
        {ilegiveis.map((f) => (
          <div key={f.id} className="aud-ficheiro aud-ficheiro-inerte">
            <span className="aud-ficheiro-nome">{f.nome_original}</span>
            <span className="aud-ficheiro-meta aud-sub-alerta">{f.erro}</span>
          </div>
        ))}
      </Pasta>
    </div>
  );
};

export default FicheirosArvore;
